import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Modal,
} from 'react-native';
import ReactDOM from 'react-dom';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { talentActivitiesApi, talentTeachersApi, talentStudentsApi, talentProjectsApi, talentSpecializationsApi } from '../../services/apiService';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks/useResponsive';
import { normalizeArabicNumbers } from '../../utils/rtl';

type RouteProps = RouteProp<MainStackParamList, 'AddTalentActivity'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: string;
  name: string;
  weight: string;
  description: string;
  kpiType: string;
  polarity: string;
  unit: string;
  frequency: string;
  formula: string;
  targetValue: string;
  dataSource: string;
  goalWeight: string;
  startDate: string;
  isExpanded: boolean;
}

interface DaySchedule {
  day: string;
  labelAr: string;
  labelEn: string;
  selected: boolean;
  startTime: string;
  endTime: string;
}

interface CustomDateEntry {
  date: string; // YYYY-MM-DD
  startTime: string;
  endTime: string;
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronDownIcon = ({ size = 16, color = '#6B7280', style }: { size?: number; color?: string; style?: any }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ClockIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps) => {
  const { colors, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
        onClick={() => setIsOpen(false)}
      />
      <div
        style={{
          position: 'fixed',
          top: buttonLayout.y + buttonLayout.height + 4,
          left: buttonLayout.x,
          width: buttonLayout.width,
          zIndex: 99999,
          backgroundColor: dropdownTheme.bg,
          border: `1px solid ${dropdownTheme.border}`,
          borderRadius: 12,
          paddingTop: 8,
          paddingBottom: 8,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
        }}
      >
        {options.map((option) => (
          <div
            key={option.value}
            style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 16, paddingRight: 16, cursor: 'pointer', textAlign: 'right', direction: 'rtl' }}
            onClick={() => { onChange(option.value); setIsOpen(false); }}
          >
            <Text style={[styles.dropdownItemText, { color: dropdownTheme.text, width: '100%' }]}>{option.label}</Text>
          </div>
        ))}
      </div>
    </>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[styles.dropdownButton, { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border }, disabled && styles.dropdownDisabled]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[styles.dropdownButtonText, { color: dropdownTheme.text }, !selectedOption && { color: dropdownTheme.placeholder }]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// Single Date Picker Component
// ============================================================

const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const AR_DAYS_SHORT = ['أح', 'إث', 'ثل', 'أر', 'خم', 'جم', 'سب'];
const EN_DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function buildMonthCells(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  return cells;
}

/* ── Inject CSS to hide select arrows globally for time picker ── */
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const id = 'time-picker-no-arrow';
  if (!document.getElementById(id)) {
    const s = document.createElement('style');
    s.id = id;
    s.textContent = '.time-picker-select { -webkit-appearance: none !important; -moz-appearance: none !important; appearance: none !important; background-image: none !important; }';
    document.head.appendChild(s);
  }
}

/* ── 12-hour Time Picker (AM/PM) ──────────────────────────── */
function TimePicker12h({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  // value is "HH:MM" in 24h format. Display as 12h with AM/PM.
  const parse = (v: string) => {
    if (!v) return { h: '', m: '', p: 'AM' };
    const [hh, mm] = v.split(':');
    let h24 = parseInt(hh || '0', 10);
    const p = h24 >= 12 ? 'PM' : 'AM';
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    return { h: String(h12), m: mm || '00', p };
  };
  const { h, m, p } = parse(value);

  const update = (newH: string, newM: string, newP: string) => {
    let h24 = parseInt(newH || '12', 10);
    if (newP === 'PM' && h24 !== 12) h24 += 12;
    if (newP === 'AM' && h24 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2, '0')}:${newM || '00'}`);
  };

  const selectStyle: any = {
    border: 'none', outline: 'none', backgroundColor: 'transparent',
    fontSize: 14, fontFamily: 'inherit', cursor: 'pointer',
    padding: '4px 2px', textAlign: 'center',
    WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, direction: 'ltr', width: '100%', justifyContent: 'flex-end' }}>
      <select className="time-picker-select" value={h || '12'} onChange={(e) => update(e.target.value, m, p)} style={selectStyle}>
        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(hh => (
          <option key={hh} value={String(Number(hh))}>{hh}</option>
        ))}
      </select>
      <span style={{ fontSize: 14, color: '#64748B' }}>:</span>
      <select className="time-picker-select" value={m} onChange={(e) => update(h, e.target.value, p)} style={selectStyle}>
        {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0')).map(mm => (
          <option key={mm} value={mm}>{mm}</option>
        ))}
      </select>
      <select className="time-picker-select" value={p} onChange={(e) => update(h, m, e.target.value)} style={{ ...selectStyle, fontWeight: 600, color: '#A9211F' }}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

interface SingleDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
}

const SingleDatePicker = ({ value, onChange, minDate, maxDate, placeholder }: SingleDatePickerProps) => {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [layout, setLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const btnRef = useRef<View>(null);

  const initMonth = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(initMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState(initMonth.getMonth());

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const handleOpen = () => {
    if (isOpen) { setIsOpen(false); return; }
    // Reset view to selected date's month, or minDate month, or today
    const d = value ? new Date(value) : (minDate ? new Date(minDate) : new Date());
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setLayout({ x, y, width: w, height: h });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const handleSelect = (dateStr: string) => {
    onChange(dateStr);
    setIsOpen(false);
  };

  const months = isRTL ? AR_MONTHS : EN_MONTHS;
  const dayHeaders = isRTL ? AR_DAYS_SHORT : EN_DAYS_SHORT;
  const cells = buildMonthCells(viewYear, viewMonth);

  const displayValue = value
    ? (() => {
        const [y, m, d] = value.split('-');
        return `${d}/${m}/${y}`;
      })()
    : '';

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
        onClick={() => setIsOpen(false)}
      />
      <div style={{
        position: 'fixed',
        top: layout.y + layout.height + 4,
        left: Math.max(8, layout.x + layout.width - 320),
        zIndex: 99999,
      }}>
        <View style={[dpStyles.panel, { backgroundColor: colors.card, borderColor: colors.border }]}>
          {/* Month nav */}
          <View style={dpStyles.monthNav}>
            <TouchableOpacity onPress={nextMonth} style={dpStyles.navBtn}>
              <ChevronDownIcon size={14} color={colors.textPrimary} style={{ transform: [{ rotate: '90deg' }] }} />
            </TouchableOpacity>
            <Text style={[dpStyles.monthLabel, { color: colors.textPrimary }]}>
              {months[viewMonth]} {viewYear}
            </Text>
            <TouchableOpacity onPress={prevMonth} style={dpStyles.navBtn}>
              <ChevronDownIcon size={14} color={colors.textPrimary} style={{ transform: [{ rotate: '-90deg' }] }} />
            </TouchableOpacity>
          </View>
          {/* Day headers */}
          <View style={dpStyles.dayHeaderRow}>
            {dayHeaders.map(h => (
              <Text key={h} style={[dpStyles.dayHeaderText, { color: colors.textMuted }]}>{h}</Text>
            ))}
          </View>
          {/* Grid */}
          <View style={dpStyles.grid}>
            {cells.map((cell, idx) => {
              const isSelected = cell.dateStr === value;
              const isToday = cell.dateStr === todayStr;
              const isDisabled = !cell.isCurrentMonth || (minDate ? cell.dateStr < minDate : false) || (maxDate ? cell.dateStr > maxDate : false);
              return (
                <TouchableOpacity
                  key={idx}
                  style={[
                    dpStyles.cell,
                    isSelected && { backgroundColor: '#a9211f', borderRadius: 20 },
                    isToday && !isSelected && { backgroundColor: isDark ? '#333' : '#F1F5F9', borderRadius: 20 },
                  ]}
                  onPress={() => !isDisabled && handleSelect(cell.dateStr)}
                  disabled={isDisabled}
                >
                  <Text style={[
                    dpStyles.cellText,
                    { color: isDisabled ? '#CBD5E1' : colors.textPrimary },
                    isSelected && { color: '#FFFFFF', fontFamily: FONTS.medium },
                  ]}>
                    {cell.day}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <View ref={btnRef}>
      <TouchableOpacity
        onPress={handleOpen}
        style={[dpStyles.button, { backgroundColor: isDark ? colors.backgroundAlt : '#F9FAFB', borderColor: colors.border }]}
      >
        <CalendarIcon size={16} color={colors.textMuted} />
        <Text style={[dpStyles.buttonText, { color: value ? colors.textPrimary : colors.textMuted }]}>
          {displayValue || placeholder || (isRTL ? 'اختر التاريخ' : 'Select date')}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

const dpStyles = StyleSheet.create({
  button: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  buttonText: { fontSize: 14, fontFamily: FONTS.regular, flex: 1, textAlign: 'right' },
  panel: { width: 320, borderRadius: 12, borderWidth: 1, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 10 },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  navBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  monthLabel: { fontSize: 16, fontFamily: FONTS.medium },
  dayHeaderRow: { flexDirection: 'row', marginBottom: 8 },
  dayHeaderText: { flex: 1, textAlign: 'center', fontSize: 12, fontFamily: FONTS.medium },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: '14.28%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 8 },
  cellText: { fontSize: 14, fontFamily: FONTS.regular },
});

// ============================================================
// Multi-Select Tags Component
// ============================================================

interface MultiSelectProps {
  values: string[];
  options: string[];
  placeholder: string;
  onChange: (values: string[]) => void;
}

const MultiSelectTags = ({ values, options, placeholder, onChange }: MultiSelectProps) => {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [confirmTag, setConfirmTag] = useState<string | null>(null);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);

  const bg = colors.card;
  const border = colors.border;
  const text = colors.textPrimary;

  const handleOpen = () => {
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  const toggleOption = (opt: string) => {
    if (values.includes(opt)) {
      onChange(values.filter(v => v !== opt));
    } else {
      onChange([...values, opt]);
    }
  };

  const removeTag = (tag: string) => {
    setConfirmTag(tag);
  };

  const confirmRemove = () => {
    if (confirmTag) {
      onChange(values.filter(v => v !== confirmTag));
      setConfirmTag(null);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
        onClick={() => setIsOpen(false)}
      />
      <div
        style={{
          position: 'fixed',
          top: buttonLayout.y + buttonLayout.height + 4,
          left: buttonLayout.x,
          width: buttonLayout.width,
          zIndex: 99999,
          backgroundColor: bg,
          border: `1px solid ${border}`,
          borderRadius: 12,
          paddingTop: 8,
          paddingBottom: 8,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
          maxHeight: 200,
          overflowY: 'auto' as const,
        }}
      >
        {options.filter(o => !values.includes(o)).map((option) => (
          <div
            key={option}
            style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 16, paddingRight: 16, cursor: 'pointer', textAlign: 'right', direction: 'rtl' }}
            onClick={() => { toggleOption(option); setIsOpen(false); }}
          >
            <Text style={[styles.dropdownItemText, { color: text, width: '100%' }]}>{option}</Text>
          </div>
        ))}
      </div>
    </>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[styles.multiSelectButton, { backgroundColor: bg, borderColor: border }]}
        onPress={handleOpen}
      >
        <ChevronDownIcon size={16} color="#6B7280" />
        <View style={styles.tagsWrap}>
          {values.length === 0 && (
            <Text style={[styles.dropdownButtonText, { color: '#9CA3AF' }]}>{placeholder}</Text>
          )}
          {values.map((tag) => (
            <TouchableOpacity key={tag} style={styles.tag} onPress={() => removeTag(tag)}>
              <Text style={styles.tagX}>×</Text>
              <Text style={styles.tagText}>{tag}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
      {portalContent}
      <Modal visible={!!confirmTag} transparent animationType="fade" onRequestClose={() => setConfirmTag(null)}>
        <View style={confirmStyles.overlay}>
          <View style={[confirmStyles.box, { backgroundColor: colors.card }]}>
            <Text style={[confirmStyles.title, { color: colors.textPrimary }]}>
              {isRTL ? 'تأكيد الإزالة' : 'Confirm Removal'}
            </Text>
            <Text style={[confirmStyles.message, { color: colors.textSecondary }]}>
              {isRTL ? `هل أنت متأكد من إزالة "${confirmTag}"؟` : `Are you sure you want to remove "${confirmTag}"?`}
            </Text>
            <View style={confirmStyles.buttons}>
              <TouchableOpacity style={confirmStyles.cancelBtn} onPress={() => setConfirmTag(null)}>
                <Text style={confirmStyles.cancelText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={confirmStyles.removeBtn} onPress={confirmRemove}>
                <Text style={confirmStyles.removeText}>{isRTL ? 'إزالة' : 'Remove'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const confirmStyles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  box: { borderRadius: 12, padding: 24, width: 340, alignItems: 'center' },
  title: { fontSize: 16, fontFamily: FONTS.medium, marginBottom: 12 },
  message: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'center', marginBottom: 20 },
  buttons: { flexDirection: 'row', gap: 12, width: '100%' },
  cancelBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' },
  cancelText: { fontSize: 14, fontFamily: FONTS.medium, color: '#414042' },
  removeBtn: { flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' },
  removeText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
});

// ============================================================
// Color options
// ============================================================
const COLOR_OPTIONS = ['#2ca02c', '#3a5aa3', '#a9211f', '#a6813f', '#9467bd', '#ff7f0e', '#1f77b4', '#e377c2'];

// ============================================================
// Mock Data
// ============================================================
// Teachers and students fetched from API (see useEffect below)

// Location is now a free-text input (no hardcoded options)

// ============================================================
// Main Component
// ============================================================

export default function AddTalentActivityScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const scrollViewRef = useRef<ScrollView>(null);
  const { isMobile, containerPadding } = useResponsive();

  const projectIdParam = route.params?.projectId;
  const specializationIdParam = route.params?.specializationId;
  const activityId = route.params?.activityId;
  const isEditMode = !!activityId;
  const isSpecLocked = !!specializationIdParam;
  const isProjectLocked = !!projectIdParam;

  const [selectedSpecializationId, setSelectedSpecializationId] = useState<number | undefined>(specializationIdParam);
  const [specializationOptions, setSpecializationOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(projectIdParam);
  const [projectOptions, setProjectOptions] = useState<{ value: string; label: string }[]>([]);
  const projectId = selectedProjectId;
  const [projectStartDate, setProjectStartDate] = useState('');
  const [projectEndDate, setProjectEndDate] = useState('');

  // Theme colors
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: isDark ? colors.cardBorder : '#E2E8F0',
    sectionTitle: colors.textPrimary,
    label: '#414042',
    input: colors.card,
    inputBorder: isDark ? colors.border : '#E2E8F0',
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : '#F1F5F9',
    cancelButton: colors.card,
    cancelButtonBorder: isDark ? colors.border : '#CBD5E1',
    cancelButtonText: colors.textMuted,
  };

  // Form state — basic info (empty defaults; edit mode fills via useEffect)
  const [name, setName] = useState('');
  const [weight, setWeight] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [identityColor, setIdentityColor] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [location, setLocation] = useState('');

  // Agenda state
  const [agendaMode, setAgendaMode] = useState<'recurring' | 'custom'>('recurring');
  const [daySchedules, setDaySchedules] = useState<DaySchedule[]>([
    { day: 'mon', labelAr: 'الاثنين', labelEn: 'Mon', selected: false, startTime: '', endTime: '' },
    { day: 'tue', labelAr: 'الثلاثاء', labelEn: 'Tue', selected: false, startTime: '', endTime: '' },
    { day: 'wed', labelAr: 'الأربعاء', labelEn: 'Wed', selected: false, startTime: '', endTime: '' },
    { day: 'thu', labelAr: 'الخميس', labelEn: 'Thu', selected: false, startTime: '', endTime: '' },
    { day: 'fri', labelAr: 'الجمعة', labelEn: 'Fri', selected: false, startTime: '', endTime: '' },
    { day: 'sat', labelAr: 'السبت', labelEn: 'Sat', selected: false, startTime: '', endTime: '' },
    { day: 'sun', labelAr: 'الأحد', labelEn: 'Sun', selected: false, startTime: '', endTime: '' },
  ]);

  // Custom dates state
  const [customDates, setCustomDates] = useState<CustomDateEntry[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Participants state
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<string[]>([]);
  const [studentOptions, setStudentOptions] = useState<string[]>([]);

  // Fetch specialization options
  useEffect(() => {
    const fetchSpecs = async () => {
      try {
        const resp = await talentSpecializationsApi.list({ page_size: 200 });
        const specs = resp?.results || resp;
        if (Array.isArray(specs)) {
          setSpecializationOptions(specs.map((s: any) => ({ value: String(s.id), label: s.name })));
        }
      } catch (e) {
        console.log('[AddActivity] Failed to fetch specializations:', e);
      }
    };
    fetchSpecs();
  }, []);

  // Fetch projects filtered by selected specialization
  useEffect(() => {
    if (!selectedSpecializationId) {
      if (!isProjectLocked) {
        setProjectOptions([]);
        setSelectedProjectId(undefined);
      }
      return;
    }
    const fetchProjects = async () => {
      try {
        const resp = await talentProjectsApi.list({ specialization: selectedSpecializationId, page_size: 200 });
        const projects = resp?.results || resp;
        if (Array.isArray(projects)) {
          setProjectOptions(projects.map((p: any) => ({ value: String(p.id), label: p.name })));
        }
      } catch (e) {
        console.log('[AddActivity] Failed to fetch projects:', e);
      }
    };
    fetchProjects();
  }, [selectedSpecializationId, isProjectLocked]);

  // Load project date range to constrain activity dates
  useEffect(() => {
    const pid = projectId || projectIdParam;
    if (!pid) { setProjectStartDate(''); setProjectEndDate(''); return; }
    (async () => {
      try {
        const proj = await talentProjectsApi.get(Number(pid));
        if (proj) {
          setProjectStartDate(proj.start_date || '');
          setProjectEndDate(proj.end_date || '');
          console.log('[AddActivity] Project dates loaded:', proj.start_date, '-', proj.end_date);
        }
      } catch (e) {
        console.log('[AddActivity] Failed to load project dates:', e);
      }
    })();
  }, [projectId, projectIdParam]);

  // Fetch real teachers from API
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const teachersResp = await talentTeachersApi.list();
        const teachers = teachersResp?.results || teachersResp;
        if (Array.isArray(teachers)) {
          setTeacherOptions(teachers.map((t: any) => t.name));
        }
      } catch (e) {
        console.log('[AddActivity] Failed to fetch teachers:', e);
      }
    };
    fetchTeachers();
  }, []);

  // Fetch students enrolled in the selected project
  useEffect(() => {
    if (!projectId) { setStudentOptions([]); return; }
    const fetchStudents = async () => {
      try {
        const studentsResp = await talentStudentsApi.list({ project: projectId, page_size: 1000 });
        const students = studentsResp?.results || studentsResp;
        if (Array.isArray(students)) {
          setStudentOptions(students.map((s: any) => s.name));
        }
      } catch (e) {
        console.log('[AddActivity] Failed to fetch project students:', e);
        setStudentOptions([]);
      }
    };
    fetchStudents();
  }, [projectId]);

  // KPIs state
  const [kpis, setKpis] = useState<KPIFormData[]>([]);

  // Track existing activities' total weight for validation
  const [usedWeight, setUsedWeight] = useState(0);
  useEffect(() => {
    const fetchExistingWeights = async () => {
      try {
        const resp = await talentActivitiesApi.list({ project: projectId });
        const activities = resp?.results || resp;
        if (Array.isArray(activities)) {
          const total = activities
            .filter((a: any) => a.id !== activityId) // exclude current in edit mode
            .reduce((sum: number, a: any) => sum + (parseFloat(a.weight) || 0), 0);
          setUsedWeight(total);
        }
      } catch (e) {
        console.log('[AddActivity] Failed to fetch existing weights:', e);
      }
    };
    fetchExistingWeights();
  }, [projectId, activityId]);

  const remainingWeight = Math.max(0, 100 - usedWeight);
  const currentWeight = parseFloat(weight) || 0;
  const weightExceeds = currentWeight > remainingWeight;

  // Edit mode: fetch activity data from API
  useEffect(() => {
    if (!activityId) return;
    const fetchActivity = async () => {
      try {
        const activity = await talentActivitiesApi.get(activityId);
        if (activity) {
          setName(activity.name || '');
          setWeight(String(activity.weight || ''));
          setDescription(activity.description || '');
          setIdentityColor(activity.identity_color || '');
          setLocation(activity.location || '');
          if (activity.project && !selectedProjectId) {
            setSelectedProjectId(activity.project);
          }
          // Resolve specialization from project
          if (activity.project && !selectedSpecializationId) {
            try {
              const proj = await talentProjectsApi.get(activity.project);
              if (proj?.specialization) {
                setSelectedSpecializationId(proj.specialization);
              }
            } catch (e) {
              console.log('[AddActivity] Failed to resolve specialization from project:', e);
            }
          }
          // Parse dates from start_at / end_at (datetime strings)
          if (activity.start_at) {
            const startDt = new Date(activity.start_at);
            setStartDate(startDt.toISOString().split('T')[0]);
          }
          if (activity.end_at) {
            const endDt = new Date(activity.end_at);
            setEndDate(endDt.toISOString().split('T')[0]);
          }

          // Load teachers
          if (Array.isArray(activity.teachers) && activity.teachers.length > 0) {
            setSelectedTeachers(activity.teachers.map((t: any) => t.name || t));
          } else if (Array.isArray(activity.teacher_names) && activity.teacher_names.length > 0) {
            setSelectedTeachers(activity.teacher_names);
          }

          // Load students
          if (Array.isArray(activity.students) && activity.students.length > 0) {
            setSelectedStudents(activity.students.map((s: any) => s.name || s));
          } else if (Array.isArray(activity.student_names) && activity.student_names.length > 0) {
            setSelectedStudents(activity.student_names);
          }

          // Load schedules into agenda
          const schedules = activity.schedules || [];
          if (schedules.length > 0) {
            // Detect if recurring (multiple dates on same weekday) or custom
            const dayMap: Record<number, { startTime: string; endTime: string }[]> = {};
            for (const s of schedules) {
              const d = new Date(s.date + 'T00:00:00');
              const dayIdx = d.getDay(); // 0=Sun
              if (!dayMap[dayIdx]) dayMap[dayIdx] = [];
              dayMap[dayIdx].push({ startTime: s.start_time?.substring(0, 5) || '', endTime: s.end_time?.substring(0, 5) || '' });
            }

            // Check if schedules have a recurring pattern (same time across same weekday)
            const weekdayMapping: Record<number, string> = { 0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat' };
            const uniqueDays = Object.keys(dayMap);
            const isRecurring = uniqueDays.length <= 7 && uniqueDays.every(dayIdx => {
              const slots = dayMap[Number(dayIdx)];
              return slots.length >= 1 && slots.every(s => s.startTime === slots[0].startTime && s.endTime === slots[0].endTime);
            });

            if (isRecurring) {
              setAgendaMode('recurring');
              setDaySchedules(prev => prev.map(ds => {
                const dayNum = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 }[ds.day] ?? -1;
                const slots = dayMap[dayNum];
                if (slots && slots.length > 0) {
                  return { ...ds, selected: true, startTime: slots[0].startTime, endTime: slots[0].endTime };
                }
                return { ...ds, selected: false };
              }));
            } else {
              setAgendaMode('custom');
              const mapped = schedules.map((s: any) => ({
                date: s.date,
                startTime: s.start_time?.substring(0, 5) || '09:00',
                endTime: s.end_time?.substring(0, 5) || '10:00',
              }));
              setCustomDates(mapped);
              if (mapped.length > 0) {
                const firstDate = new Date(mapped[0].date + 'T00:00:00');
                setCalendarMonth(new Date(firstDate.getFullYear(), firstDate.getMonth(), 1));
              }
            }
          }

          // Load KPIs
          const kpiLinks = activity.kpi_links || [];
          if (kpiLinks.length > 0) {
            setKpis(kpiLinks.map((k: any) => ({
              id: String(k.id || `existing_${Date.now()}_${Math.random()}`),
              name: k.kpi_name || k.name || '',
              weight: String(k.weight || ''),
              description: k.description || '',
              kpiType: k.kpi_type || '',
              polarity: k.polarity || '',
              unit: k.unit || '',
              frequency: k.frequency || '',
              formula: k.formula || '',
              targetValue: String(k.target_value || ''),
              dataSource: k.data_source || '',
              goalWeight: String(k.goal_weight || ''),
              startDate: k.start_date || '',
              isExpanded: false,
            })));
          }
        }
      } catch (e) {
        console.log('[AddTalentActivity] Failed to fetch activity for edit:', e);
      }
    };
    fetchActivity();
  }, [activityId]);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);
  const [saving, setSaving] = useState(false);

  // KPI Options
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: t('kpis.strategic') },
    { value: 'operational', label: t('kpis.operational') },
  ];
  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];
  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];
  const frequencyOptions: DropdownOption[] = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  const generateId = () => `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addKpi = () => {
    setKpis([...kpis, {
      id: generateId(), name: '', weight: '', description: '', kpiType: '', polarity: '',
      unit: '', frequency: '', formula: '', targetValue: '', dataSource: '', goalWeight: '',
      startDate: '', isExpanded: true,
    }]);
  };

  const updateKpi = (id: string, field: keyof KPIFormData, value: any) => {
    setKpis(kpis.map(kpi => (kpi.id === id ? { ...kpi, [field]: value } : kpi)));
  };

  const removeKpi = (id: string) => {
    setKpis(kpis.filter(kpi => kpi.id !== id));
  };

  const toggleKpiExpansion = (id: string) => {
    setKpis(kpis.map(kpi => (kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi)));
  };

  // Compute which days of the week fall within startDate–endDate range
  const allowedDaysKey = `${startDate}_${endDate}`;
  const allowedDays = useMemo(() => {
    if (!startDate || !endDate) return new Set(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
    const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const allowed = new Set<string>();
    const start = new Date(startDate);
    const end = new Date(endDate);
    const cursor = new Date(start);
    const maxDays = Math.min(Math.ceil((end.getTime() - start.getTime()) / 86400000) + 1, 7);
    for (let i = 0; i < maxDays; i++) {
      allowed.add(dayMap[cursor.getDay()]);
      cursor.setDate(cursor.getDate() + 1);
    }
    return allowed;
  }, [startDate, endDate]);

  // Deselect days that are no longer in range when dates change
  useEffect(() => {
    setDaySchedules(prev => {
      const updated = prev.map(d =>
        d.selected && !allowedDays.has(d.day) ? { ...d, selected: false } : d
      );
      // Only update state if something actually changed
      if (updated.some((d, i) => d.selected !== prev[i].selected)) return updated;
      return prev;
    });
  }, [allowedDaysKey]);

  const toggleDay = (dayKey: string) => {
    if (!allowedDays.has(dayKey)) return;
    setDaySchedules(prev => prev.map(d =>
      d.day === dayKey ? {
        ...d,
        selected: !d.selected,
        // Auto-populate default times when selecting a day
        startTime: !d.selected && !d.startTime ? '09:00' : d.startTime,
        endTime: !d.selected && !d.endTime ? '10:00' : d.endTime,
      } : d
    ));
  };

  const updateDayTime = (dayKey: string, field: 'startTime' | 'endTime', value: string) => {
    setDaySchedules(prev => prev.map(d =>
      d.day === dayKey ? { ...d, [field]: value } : d
    ));
  };

  // Custom date helpers
  const toggleCustomDate = (dateStr: string) => {
    if (customDates.find(d => d.date === dateStr)) {
      setCustomDates(customDates.filter(d => d.date !== dateStr));
    } else {
      setCustomDates([...customDates, { date: dateStr, startTime: '09:00', endTime: '10:00' }]);
    }
  };

  const updateCustomDateTime = (dateStr: string, field: 'startTime' | 'endTime', value: string) => {
    setCustomDates(customDates.map(d => d.date === dateStr ? { ...d, [field]: value } : d));
  };

  const removeCustomDate = (dateStr: string) => {
    setCustomDates(customDates.filter(d => d.date !== dateStr));
  };

  const formatDateArabic = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    const monthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
    return `${dayNames[date.getDay()]} - ${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatDateEnglish = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${dayNames[date.getDay()]} - ${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDaySun = new Date(year, month, 1).getDay(); // 0=Sun
    const firstDay = firstDaySun === 0 ? 6 : firstDaySun - 1; // Convert to 0=Mon, 6=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];

    // Previous month trailing days
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const m = month === 0 ? 11 : month - 1;
      const y = month === 0 ? year - 1 : year;
      cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
    }

    // Next month leading days to fill grid
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const m = month === 11 ? 0 : month + 1;
      const y = month === 11 ? year + 1 : year;
      cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
    }

    return cells;
  };

  const arabicMonthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  const englishMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const [dateError, setDateError] = useState('');

  const handleSave = async () => {
    if (!name.trim() || !startDate || !endDate || !selectedSpecializationId || !projectId) return;
    if (weightExceeds) return;
    // Validate end date is after start date
    if (new Date(endDate) < new Date(startDate)) {
      setDateError(isRTL ? 'تاريخ نهاية النشاط يجب أن يكون بعد أو مساوياً لتاريخ البداية' : 'End date must be on or after start date');
      return;
    }
    // Validate activity dates within project date range
    if (projectStartDate && new Date(startDate) < new Date(projectStartDate)) {
      setDateError(isRTL
        ? `تاريخ بداية النشاط يجب أن يكون بعد تاريخ بداية المشروع (${projectStartDate})`
        : `Activity start date must be on or after project start date (${projectStartDate})`);
      return;
    }
    if (projectEndDate && new Date(endDate) > new Date(projectEndDate)) {
      setDateError(isRTL
        ? `تاريخ نهاية النشاط يجب أن يكون قبل تاريخ نهاية المشروع (${projectEndDate})`
        : `Activity end date must be on or before project end date (${projectEndDate})`);
      return;
    }
    setDateError('');
    try {
      setSaving(true);
      const selectedDays = daySchedules.filter(d => d.selected);
      const firstDay = selectedDays[0];
      const activityData: any = {
        project: projectId,
        name: name.trim(),
        description: description.trim(),
        weight: parseFloat(weight) || null,
        identity_color: identityColor,
        location: location,
        start_at: startDate ? `${startDate}T${firstDay?.startTime || '09:00'}:00` : null,
        end_at: endDate ? `${endDate}T${firstDay?.endTime || '10:00'}:00` : null,
        category: 'workshop',
        teacher_names: selectedTeachers,
        student_names: selectedStudents,
      };
      let savedActivityId: number;
      if (isEditMode && activityId) {
        await talentActivitiesApi.update(activityId, activityData);
        savedActivityId = activityId;
      } else {
        const created = await talentActivitiesApi.create(activityData);
        savedActivityId = created.id;
      }

      // Save schedules
      let schedulesToSave: { date: string; start_time: string; end_time: string }[] = [];

      if (agendaMode === 'custom' && customDates.length > 0) {
        schedulesToSave = customDates.map(d => ({
          date: d.date,
          start_time: d.startTime || '09:00',
          end_time: d.endTime || '10:00',
        }));
      } else if (agendaMode === 'recurring' && selectedDays.length > 0 && startDate && endDate) {
        // Generate actual dates from recurring day selections
        const dayKeyToNum: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
        const start = new Date(startDate + 'T00:00:00');
        const end = new Date(endDate + 'T00:00:00');
        const cursor = new Date(start);
        while (cursor <= end) {
          const dayNum = cursor.getDay();
          const matchingDay = selectedDays.find(d => dayKeyToNum[d.day] === dayNum);
          if (matchingDay && matchingDay.startTime && matchingDay.endTime) {
            const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
            schedulesToSave.push({
              date: dateStr,
              start_time: matchingDay.startTime,
              end_time: matchingDay.endTime,
            });
          }
          cursor.setDate(cursor.getDate() + 1);
        }
      }

      if (schedulesToSave.length > 0) {
        await talentActivitiesApi.setSchedules(savedActivityId, schedulesToSave);
      }

      navigation.goBack();
    } catch (error: any) {
      console.error('[AddTalentActivity] Save error:', error);
      const errData = error?.response?.data;
      if (errData) console.error('[AddTalentActivity] Server response:', JSON.stringify(errData));
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = isEditMode
    ? (isRTL ? 'تعديل النشاط' : 'Edit Activity')
    : (isRTL ? 'إضافة نشاط' : 'Add Activity');

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[styles.content, { padding: containerPadding }]}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: isRTL ? 'مبادرة مواهب' : 'Talents' },
            { label: pageTitle },
          ]}
        />

        {/* Main Card */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>
            {pageTitle}
          </Text>

          {/* ============================================ */}
          {/* Section 1: Basic Information */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
            </Text>

            {/* Specialization + Project selectors */}
            <View style={[styles.row, isMobile && styles.rowMobile]}>
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'الموهبة' : 'Specialization'}
                  </Text>
                </View>
                <Dropdown
                  value={selectedSpecializationId ? String(selectedSpecializationId) : ''}
                  options={specializationOptions}
                  placeholder={isRTL ? 'اختر الموهبة' : 'Select specialization'}
                  onChange={(val) => {
                    setSelectedSpecializationId(Number(val));
                    if (!isProjectLocked) {
                      setSelectedProjectId(undefined);
                    }
                  }}
                  disabled={isSpecLocked}
                />
              </View>
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'المشروع' : 'Project'}
                  </Text>
                </View>
                <Dropdown
                  value={selectedProjectId ? String(selectedProjectId) : ''}
                  options={projectOptions}
                  placeholder={isRTL ? 'اختر المشروع' : 'Select project'}
                  onChange={(val) => setSelectedProjectId(Number(val))}
                  disabled={isProjectLocked}
                />
              </View>
            </View>

            {/* Row 1: Name + Weight */}
            <View style={[styles.row, isMobile && styles.rowMobile]}>
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'الوزن' : 'Weight'}
                  </Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: weightExceeds ? '#DC2626' : themeColors.inputBorder, color: themeColors.inputText }]}
                    value={weight}
                    onChangeText={(v) => setWeight(normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
                <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: weightExceeds ? '#DC2626' : themeColors.placeholder, textAlign: 'right', marginTop: 4 }}>
                  {isRTL
                    ? `المتبقي من وزن المشروع: %${remainingWeight}${usedWeight > 0 ? ` (مستخدم: %${usedWeight})` : ''}`
                    : `Remaining: ${remainingWeight}%${usedWeight > 0 ? ` (Used: ${usedWeight}%)` : ''}`}
                </Text>
                {weightExceeds && (
                  <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: '#DC2626', textAlign: 'right', marginTop: 2 }}>
                    {isRTL
                      ? 'مجموع أوزان الأنشطة لا يمكن أن يتجاوز %100'
                      : 'Total activity weights cannot exceed 100%'}
                  </Text>
                )}
              </View>

              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'اسم النشاط' : 'Activity Name'}
                  </Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={name}
                  onChangeText={setName}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Row 2: Description */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {isRTL ? 'وصف النشاط' : 'Activity Description'}
                </Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={description}
                onChangeText={setDescription}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Row 3: Start Date + End Date */}
            <View style={[styles.row, isMobile && styles.rowMobile]}>
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'تاريخ نهاية النشاط' : 'End Date'}
                  </Text>
                </View>
                <SingleDatePicker
                  value={endDate}
                  onChange={(val) => {
                    setEndDate(val);
                    if (startDate && val && new Date(val) < new Date(startDate)) {
                      setDateError(isRTL ? 'تاريخ نهاية النشاط يجب أن يكون بعد أو مساوياً لتاريخ البداية' : 'End date must be on or after start date');
                    } else { setDateError(''); }
                  }}
                  minDate={startDate || projectStartDate || new Date().toISOString().split('T')[0]}
                  maxDate={projectEndDate || undefined}
                  placeholder={isRTL ? 'اختر تاريخ النهاية' : 'Select end date'}
                />
                {dateError ? (
                  <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: '#DC2626', textAlign: 'right', marginTop: 4 }}>{dateError}</Text>
                ) : null}
              </View>
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'تاريخ بداية النشاط' : 'Start Date'}
                  </Text>
                </View>
                <SingleDatePicker
                  value={startDate}
                  onChange={(val) => {
                    setStartDate(val);
                    if (val) {
                      const d = new Date(val + 'T00:00:00');
                      setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                    }
                    if (endDate && val && new Date(endDate) < new Date(val)) {
                      setDateError(isRTL ? 'تاريخ نهاية النشاط يجب أن يكون بعد أو مساوياً لتاريخ البداية' : 'End date must be on or after start date');
                    } else { setDateError(''); }
                  }}
                  minDate={projectStartDate || new Date().toISOString().split('T')[0]}
                  maxDate={projectEndDate || undefined}
                  placeholder={isRTL ? 'اختر تاريخ البداية' : 'Select start date'}
                />
              </View>
            </View>

            {/* Date range hint from project */}
            {projectStartDate && projectEndDate && (
              <Text style={{ fontSize: 12, fontFamily: 'Cairo-Regular', color: '#64748B', textAlign: 'right', marginTop: -4 }}>
                {isRTL
                  ? `الفترة المسموحة للنشاط: ${projectStartDate} إلى ${projectEndDate}`
                  : `Allowed activity dates: ${projectStartDate} to ${projectEndDate}`}
              </Text>
            )}

            {/* Row 4: Identity Color + Location */}
            <View style={[styles.row, isMobile && styles.rowMobile]}>
              <View style={[styles.field, { flex: 1 }]}>
                <Text style={[styles.label, { color: themeColors.label, textAlign: 'right' }]}>
                  {isRTL ? 'الموقع' : 'Location'}
                </Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={location}
                  onChangeText={setLocation}
                  placeholder={isRTL ? 'أدخل الموقع' : 'Enter location'}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'اللون التعريفي' : 'Identity Color'}
                  </Text>
                </View>
                <TouchableOpacity
                  style={[styles.colorRow, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}
                  onPress={() => setShowColorPicker(!showColorPicker)}
                  activeOpacity={0.7}
                >
                  <View style={[styles.colorSwatch, { backgroundColor: identityColor || '#2ca02c' }]} />
                </TouchableOpacity>
                {showColorPicker && (
                  <View style={styles.colorPalette}>
                    {COLOR_OPTIONS.map((color) => (
                      <TouchableOpacity
                        key={color}
                        style={[
                          styles.colorOption,
                          { backgroundColor: color },
                          identityColor === color && styles.colorOptionSelected,
                        ]}
                        onPress={() => { setIdentityColor(color); setShowColorPicker(false); }}
                      />
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>

          {/* ============================================ */}
          {/* Section 2: Agenda (only when start & end dates are set) */}
          {/* ============================================ */}
          {startDate && endDate ? (
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'الأجندة' : 'Agenda'}
            </Text>

            {/* Mode Toggle */}
            <View style={styles.agendaToggle}>
              <TouchableOpacity
                style={[styles.agendaToggleBtn, agendaMode === 'custom' ? styles.agendaToggleBtnActive : styles.agendaToggleBtnInactive]}
                onPress={() => {
                  setAgendaMode('custom');
                  // Clear recurring data when switching to custom
                  setDaySchedules(prev => prev.map(d => ({ ...d, selected: false, startTime: '', endTime: '' })));
                }}
              >
                <Text style={[styles.agendaToggleText, agendaMode === 'custom' ? styles.agendaToggleTextActive : styles.agendaToggleTextInactive]}>
                  {isRTL ? 'أوقات وتواريخ مخصصة' : 'Custom Dates'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.agendaToggleBtn, agendaMode === 'recurring' ? styles.agendaToggleBtnActive : styles.agendaToggleBtnInactive]}
                onPress={() => {
                  setAgendaMode('recurring');
                  // Clear custom data when switching to recurring
                  setCustomDates([]);
                }}
              >
                <Text style={[styles.agendaToggleText, agendaMode === 'recurring' ? styles.agendaToggleTextActive : styles.agendaToggleTextInactive]}>
                  {isRTL ? 'أوقات وتواريخ متكررة' : 'Recurring'}
                </Text>
              </TouchableOpacity>
            </View>

            {agendaMode === 'recurring' && (
              <>
                {/* Day Selection Pills */}
                <View style={styles.dayPillsRow}>
                  {daySchedules.map((d) => {
                    const isAllowed = allowedDays.has(d.day);
                    return (
                      <TouchableOpacity
                        key={d.day}
                        style={[styles.dayPill, d.selected && styles.dayPillSelected, !isAllowed && { opacity: 0.35 }]}
                        onPress={() => toggleDay(d.day)}
                        disabled={!isAllowed}
                      >
                        <Text style={[styles.dayPillText, d.selected && styles.dayPillTextSelected]}>
                          {isRTL ? d.labelAr : d.labelEn}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {/* Schedule Rows for Selected Days */}
                {daySchedules.filter(d => d.selected).map((d) => (
                  <View key={d.day} style={styles.scheduleRow}>
                    <View style={[styles.field, { flex: 1 }]}>
                      <View style={styles.labelRow}>
                        <Text style={styles.requiredStar}>*</Text>
                        <Text style={[styles.label, { color: themeColors.label }]}>
                          {isRTL ? 'وقت النهاية' : 'End Time'}
                        </Text>
                      </View>
                      <View style={[styles.timeInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                        {Platform.OS === 'web' ? (
                          <TimePicker12h value={d.endTime} onChange={(v) => updateDayTime(d.day, 'endTime', v)} />
                        ) : (
                          <>
                            <ClockIcon size={16} color={themeColors.iconColor} />
                            <TextInput style={[styles.dateInput, { color: themeColors.inputText }]} value={d.endTime} onChangeText={(v) => updateDayTime(d.day, 'endTime', v)} placeholder="HH:MM" textAlign="right" />
                          </>
                        )}
                      </View>
                    </View>

                    <View style={[styles.field, { flex: 1 }]}>
                      <View style={styles.labelRow}>
                        <Text style={styles.requiredStar}>*</Text>
                        <Text style={[styles.label, { color: themeColors.label }]}>
                          {isRTL ? 'وقت البداية' : 'Start Time'}
                        </Text>
                      </View>
                      <View style={[styles.timeInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                        {Platform.OS === 'web' ? (
                          <TimePicker12h value={d.startTime} onChange={(v) => updateDayTime(d.day, 'startTime', v)} />
                        ) : (
                          <>
                            <ClockIcon size={16} color={themeColors.iconColor} />
                            <TextInput style={[styles.dateInput, { color: themeColors.inputText }]} value={d.startTime} onChangeText={(v) => updateDayTime(d.day, 'startTime', v)} placeholder="HH:MM" textAlign="right" />
                          </>
                        )}
                      </View>
                    </View>

                    <View style={styles.dayLabel}>
                      <Text style={styles.dayLabelText}>
                        {isRTL ? d.labelAr : d.labelEn}
                      </Text>
                    </View>
                  </View>
                ))}
              </>
            )}

            {agendaMode === 'custom' && (
              <View style={[styles.row, isMobile && styles.rowMobile]}>
                {/* Date Cards - Left */}
                <ScrollView style={{ flex: 1, maxHeight: 400 }} contentContainerStyle={{ gap: 12 }}>
                  {customDates.length === 0 && (
                    <Text style={[styles.label, { color: themeColors.placeholder, textAlign: 'center', paddingVertical: 20 }]}>
                      {isRTL ? 'اختر تواريخ من التقويم' : 'Select dates from calendar'}
                    </Text>
                  )}
                  {customDates.map((cd) => (
                    <View key={cd.date} style={[styles.customDateCard, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                      <View style={styles.customDateCardHeader}>
                        <TouchableOpacity onPress={() => removeCustomDate(cd.date)} style={styles.customDateRemoveBtn}>
                          <Text style={{ fontSize: 18, color: themeColors.iconColor }}>✕</Text>
                        </TouchableOpacity>
                        <Text style={[styles.customDateTitle, { color: themeColors.inputText }]}>
                          {isRTL ? formatDateArabic(cd.date) : formatDateEnglish(cd.date)}
                        </Text>
                      </View>
                      <View style={[styles.row, { gap: 12 }]}>
                        <View style={[styles.field, { flex: 1 }]}>
                          <View style={styles.labelRow}>
                            <Text style={styles.requiredStar}>*</Text>
                            <Text style={[styles.label, { color: themeColors.label }]}>
                              {isRTL ? 'وقت النهاية' : 'End Time'}
                            </Text>
                          </View>
                          <View style={[styles.timeInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                            {Platform.OS === 'web' ? (
                              <TimePicker12h value={cd.endTime} onChange={(v) => updateCustomDateTime(cd.date, 'endTime', v)} />
                            ) : (
                              <>
                                <ClockIcon size={16} color={themeColors.iconColor} />
                                <TextInput style={[styles.dateInput, { color: themeColors.inputText }]} value={cd.endTime} onChangeText={(v) => updateCustomDateTime(cd.date, 'endTime', v)} placeholder="HH:MM" textAlign="right" />
                              </>
                            )}
                          </View>
                        </View>
                        <View style={[styles.field, { flex: 1 }]}>
                          <View style={styles.labelRow}>
                            <Text style={styles.requiredStar}>*</Text>
                            <Text style={[styles.label, { color: themeColors.label }]}>
                              {isRTL ? 'وقت البداية' : 'Start Time'}
                            </Text>
                          </View>
                          <View style={[styles.timeInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                            {Platform.OS === 'web' ? (
                              <TimePicker12h value={cd.startTime} onChange={(v) => updateCustomDateTime(cd.date, 'startTime', v)} />
                            ) : (
                              <>
                                <ClockIcon size={16} color={themeColors.iconColor} />
                                <TextInput style={[styles.dateInput, { color: themeColors.inputText }]} value={cd.startTime} onChangeText={(v) => updateCustomDateTime(cd.date, 'startTime', v)} placeholder="HH:MM" textAlign="right" />
                              </>
                            )}
                          </View>
                        </View>
                      </View>
                    </View>
                  ))}
                </ScrollView>

                {/* Calendar - Right */}
                <View style={[styles.calendarContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                  {/* Month Header */}
                  <View style={styles.calendarHeader}>
                    <TouchableOpacity onPress={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}>
                      <Text style={{ fontSize: 18, color: themeColors.iconColor }}>‹</Text>
                    </TouchableOpacity>
                    <Text style={[styles.calendarMonthText, { color: themeColors.inputText }]}>
                      {isRTL
                        ? `${arabicMonthNames[calendarMonth.getMonth()]} ${calendarMonth.getFullYear()}`
                        : `${englishMonthNames[calendarMonth.getMonth()]} ${calendarMonth.getFullYear()}`}
                    </Text>
                    <TouchableOpacity onPress={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}>
                      <Text style={{ fontSize: 18, color: themeColors.iconColor }}>›</Text>
                    </TouchableOpacity>
                  </View>

                  {/* Day Headers */}
                  <View style={styles.calendarDayHeaders}>
                    {(isRTL
                      ? ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد']
                      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                    ).map((day) => (
                      <Text key={day} style={[styles.calendarDayHeaderText, { color: themeColors.iconColor }]}>{day}</Text>
                    ))}
                  </View>

                  {/* Calendar Grid */}
                  <View style={styles.calendarGrid}>
                    {getCalendarDays().map((cell, idx) => {
                      const isSelected = customDates.some(d => d.date === cell.dateStr);
                      const today = new Date();
                      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                      const isToday = cell.dateStr === todayStr;
                      const isPast = cell.dateStr < todayStr;
                      const isBeforeStart = startDate ? cell.dateStr < startDate : false;
                      const isAfterEnd = endDate ? cell.dateStr > endDate : false;
                      const isDisabled = !cell.isCurrentMonth || isPast || isBeforeStart || isAfterEnd;
                      return (
                        <TouchableOpacity
                          key={idx}
                          style={[
                            styles.calendarCell,
                            isSelected && styles.calendarCellSelected,
                            isToday && !isSelected && styles.calendarCellToday,
                          ]}
                          onPress={() => !isDisabled && toggleCustomDate(cell.dateStr)}
                          disabled={isDisabled}
                        >
                          <Text style={[
                            styles.calendarCellText,
                            { color: isDisabled ? '#CBD5E1' : themeColors.inputText },
                            isSelected && styles.calendarCellTextSelected,
                          ]}>
                            {cell.day}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              </View>
            )}
          </View>
          ) : null}

          {/* ============================================ */}
          {/* Section 3: Participants */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'سجل المشاركين' : 'Participants Registry'}
            </Text>

            {/* Coordinators */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label, textAlign: 'right' }]}>
                {isRTL ? 'المنسقون' : 'Coordinators'}
              </Text>
              <MultiSelectTags
                values={selectedTeachers}
                options={teacherOptions}
                placeholder={isRTL ? 'اختر المنسقين' : 'Select coordinators'}
                onChange={setSelectedTeachers}
              />
            </View>

            {/* Students */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label, textAlign: 'right' }]}>
                {isRTL ? 'الطلاب المشاركون' : 'Participating Students'}
              </Text>
              <MultiSelectTags
                values={selectedStudents}
                options={studentOptions}
                placeholder={isRTL ? 'اختر الطلاب' : 'Select students'}
                onChange={setSelectedStudents}
              />
            </View>
          </View>

          {/* ============================================ */}
          {/* Section 4: KPIs */}
          {/* ============================================ */}
          <View style={[styles.kpiSection, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <TouchableOpacity
              style={styles.kpiSectionHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>
                {isRTL ? 'مؤشرات الأداء' : 'Performance Indicators'}
              </Text>
            </TouchableOpacity>

            {kpiSectionExpanded && (
              <View style={styles.kpiSectionContent}>
                {kpis.map((kpi, index) => (
                  <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
                    <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.kpiCardBorder }]}>
                      <View style={styles.kpiCardHeaderLeft}>
                        <TouchableOpacity style={styles.kpiHeaderButton} onPress={() => toggleKpiExpansion(kpi.id)}>
                          {kpi.isExpanded ? <ChevronUpIcon size={20} color={themeColors.iconColor} /> : <ChevronDownIcon size={20} color={themeColors.iconColor} />}
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.kpiHeaderButton} onPress={() => removeKpi(kpi.id)}>
                          <TrashIcon size={18} color={themeColors.iconColor} />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.kpiCardHeaderRight}>
                        <Text style={[styles.kpiCardName, { color: themeColors.inputText }]}>
                          {kpi.name || `${isRTL ? 'اسم المؤشر' : 'KPI Name'}`}
                        </Text>
                        <View style={[styles.kpiCardNumber, { backgroundColor: themeColors.kpiNumber }]}>
                          <Text style={[styles.kpiCardNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
                        </View>
                        <Text style={{ fontSize: 16, color: themeColors.iconColor }}>≡</Text>
                      </View>
                    </View>

                    {kpi.isExpanded && (
                      <View style={styles.kpiCardContent}>
                        <View style={[styles.subsection, { backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>
                            {t('kpis.basicInformation')}
                          </Text>
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            <View style={styles.fieldSmall}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                              </View>
                              <View style={styles.weightInputRow}>
                                <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                                  <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                                </TouchableOpacity>
                                <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                                <TextInput
                                  style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                  value={kpi.weight}
                                  onChangeText={(v) => updateKpi(kpi.id, 'weight', normalizeArabicNumbers(v))}
                                  keyboardType="numeric"
                                  textAlign="right"
                                />
                              </View>
                            </View>
                            <View style={styles.fieldLarge}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={kpi.name}
                                onChangeText={(v) => updateKpi(kpi.id, 'name', v)}
                                placeholder=""
                                textAlign="right"
                              />
                            </View>
                          </View>
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.description')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={kpi.description}
                              onChangeText={(v) => updateKpi(kpi.id, 'description', v)}
                              placeholder=""
                              textAlign="right"
                              multiline
                              numberOfLines={3}
                            />
                          </View>
                        </View>
                      </View>
                    )}
                  </View>
                ))}

                <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={addKpi}>
                  <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>
                    {isRTL ? 'إضافة مؤشر أداء' : 'Add KPI'}
                  </Text>
                  <PlusIcon size={16} color={themeColors.inputText} />
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{t('common.save')}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]}
            onPress={() => navigation.goBack()}
            disabled={saving}
          >
            <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  content: { paddingBottom: 48 },
  mainCard: { backgroundColor: '#FFFFFF', borderRadius: 8, padding: 20, borderWidth: 1, borderColor: '#E2E8F0', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1, gap: 20 },
  pageTitle: { fontSize: 16, fontFamily: FONTS.bold, color: '#414042', textAlign: 'right' },
  section: { backgroundColor: '#F1F5F9', borderRadius: 4, padding: 24, borderWidth: 1, borderColor: '#E2E8F0', gap: 16 },
  sectionTitle: { fontSize: 16, fontFamily: FONTS.bold, color: '#414042', textAlign: 'right', marginBottom: 8 },
  row: { flexDirection: 'row', gap: 12 },
  rowMobile: { flexDirection: 'column' },
  field: { gap: 6 },
  fieldLarge: { flex: 2, gap: 6 },
  fieldSmall: { flex: 1, gap: 6 },
  fieldHalf: { flex: 1, gap: 6 },
  labelRow: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'flex-start', gap: 2 },
  label: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042', textAlign: 'right' },
  requiredStar: { fontSize: 14, color: '#D62728', fontFamily: FONTS.regular },
  input: { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, fontFamily: FONTS.regular, color: '#414042', textAlign: 'right', minHeight: 36, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.02, shadowRadius: 1 },
  textArea: { minHeight: 60, textAlignVertical: 'top' },
  weightInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8, width: '100%' as any },
  weightInput: { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, fontFamily: FONTS.regular, color: '#414042', flex: 1, textAlign: 'center' as any },
  percentSymbol: { fontSize: 14, fontFamily: FONTS.regular, color: '#64748B' },
  setWeightButton: { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  setWeightButtonText: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042' },
  dateInputContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, gap: 8 },
  timeInputContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, gap: 8, ...(Platform.OS === 'web' ? { overflow: 'visible' as any } : {}) },
  dateInput: { flex: 1, paddingVertical: 10, fontSize: 14, fontFamily: FONTS.regular, color: '#414042', textAlign: 'right' },
  dropdownContainer: { position: 'relative' as const },
  dropdownButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, minHeight: 36 },
  dropdownDisabled: { opacity: 0.6 },
  dropdownButtonText: { flex: 1, fontSize: 14, fontFamily: FONTS.regular, color: '#414042', textAlign: 'right' },
  dropdownItemText: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042', textAlign: 'right' },
  // Multi-select
  multiSelectButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, minHeight: 36, gap: 8 },
  tagsWrap: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' },
  tag: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F1F5F9', borderRadius: 4, paddingHorizontal: 8, paddingVertical: 4, gap: 4 },
  tagText: { fontSize: 12, fontFamily: FONTS.regular, color: '#414042' },
  tagX: { fontSize: 14, color: '#64748B', fontWeight: '600' },
  // Color row
  colorRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, justifyContent: 'flex-end' },
  colorSwatch: { width: 24, height: 24, borderRadius: 4 },
  colorPalette: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, paddingTop: 8 },
  colorOption: { width: 32, height: 32, borderRadius: 6 },
  colorOptionSelected: { borderWidth: 3, borderColor: '#171717' },
  // Agenda
  agendaToggle: { flexDirection: 'row', borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: '#E2E8F0' },
  agendaToggleBtn: { flex: 1, paddingVertical: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF' },
  agendaToggleBtnActive: { backgroundColor: '#A6813F' },
  agendaToggleBtnInactive: { backgroundColor: '#FFFFFF' },
  agendaToggleText: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042' },
  agendaToggleTextActive: { color: '#FFFFFF' },
  agendaToggleTextInactive: { color: '#414042' },
  dayPillsRow: { flexDirection: 'row-reverse', gap: 8, justifyContent: 'center', flexWrap: 'wrap' },
  dayPill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' },
  dayPillSelected: { backgroundColor: '#414042', borderColor: '#414042' },
  dayPillText: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042' },
  dayPillTextSelected: { color: '#FFFFFF' },
  scheduleRow: { flexDirection: 'row', gap: 12, alignItems: 'flex-end' },
  dayLabel: { backgroundColor: '#414042', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 10, minHeight: 36, justifyContent: 'center', alignItems: 'center' },
  dayLabelText: { fontSize: 14, fontFamily: FONTS.regular, color: '#FFFFFF' },
  // Custom Dates Calendar
  customDateCard: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 12,
  },
  customDateCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  customDateTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    textAlign: 'right',
  },
  customDateRemoveBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarContainer: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 16,
  },
  calendarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  calendarMonthText: {
    fontSize: 16,
    fontFamily: FONTS.bold,
  },
  calendarDayHeaders: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  calendarDayHeaderText: {
    flex: 1,
    textAlign: 'center',
    fontSize: 12,
    fontFamily: FONTS.medium,
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  calendarCell: {
    width: '14.28%' as any,
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarCellSelected: {
    backgroundColor: '#64748B',
    borderRadius: 20,
  },
  calendarCellToday: {
    backgroundColor: '#E2E8F0',
    borderRadius: 20,
  },
  calendarCellText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  calendarCellTextSelected: {
    color: '#FFFFFF',
  },
  // KPI Section
  kpiSection: { backgroundColor: '#F1F5F9', borderRadius: 4, borderWidth: 1, borderColor: '#E2E8F0' },
  kpiSectionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16, paddingHorizontal: 16, gap: 8, minHeight: 44 },
  kpiSectionTitle: { fontSize: 16, fontFamily: FONTS.bold, color: '#414042', textAlign: 'center' },
  kpiSectionContent: { padding: 16, paddingTop: 0, gap: 12 },
  kpiCard: { backgroundColor: '#FFFFFF', borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0' },
  kpiCardHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, paddingVertical: 12, backgroundColor: '#FFFFFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  kpiCardHeaderLeft: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  kpiCardHeaderRight: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  kpiHeaderButton: { width: 44, height: 44, justifyContent: 'center', alignItems: 'center' },
  kpiCardNumber: { width: 28, height: 28, borderRadius: 6, backgroundColor: '#F1F5F9', alignItems: 'center', justifyContent: 'center' },
  kpiCardNumberText: { fontSize: 14, fontFamily: FONTS.medium, color: '#414042' },
  kpiCardName: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042', textAlign: 'right' },
  kpiCardContent: { padding: 16, gap: 16 },
  subsection: { backgroundColor: '#F1F5F9', borderRadius: 8, padding: 16, gap: 16 },
  subsectionTitle: { fontSize: 14, fontFamily: FONTS.medium, color: '#414042', textAlign: 'center', marginBottom: 8 },
  addKpiButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, paddingHorizontal: 16, borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, borderStyle: 'dashed', backgroundColor: '#FFFFFF', minHeight: 44 },
  addKpiButtonText: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042' },
  actionButtons: { flexDirection: 'row', gap: 12, marginTop: 24 },
  saveButton: { backgroundColor: '#A9211F', borderRadius: 8, paddingHorizontal: 24, paddingVertical: 12, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  saveButtonText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
  saveButtonDisabled: { backgroundColor: '#D1D5DB' },
  cancelButton: { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8, paddingHorizontal: 24, paddingVertical: 12, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  cancelButtonText: { fontSize: 14, fontFamily: FONTS.regular, color: '#414042' },
});
